import { useState } from "react";
import { useSearch } from "wouter";
import { trackEvent } from "@/lib/analytics";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useLookupReports, getLookupReportsQueryKey, useRequestReportLookupLink, useListStatuses, useListCategories, Status, Category, ApiError } from "@workspace/api-client-react";
import Layout from "@/components/Layout";
import PageHead from "@/components/PageHead";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Mail, MessageSquare, Clock, ShieldAlert, FileText, ArrowRight, CheckCircle2, Tag, Send } from "lucide-react";
import AttachmentList from "@/components/AttachmentList";
import { format } from "date-fns";

const lookupSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

type LookupFormValues = z.infer<typeof lookupSchema>;

type ReportEvent = {
  id: number;
  eventType: "report_created" | "status_changed" | "comment_added";
  description: string;
  detail?: Record<string, unknown> | null;
  createdAt: Date | string;
};

type Comment = {
  id: number;
  reportId: number;
  body: string;
  authorName: string;
  isStaff: boolean;
  createdAt: Date | string;
};

type TimelineEntry =
  | { kind: "milestone"; icon: "created" | "status" | "resolved"; description: string; createdAt: Date }
  | { kind: "comment"; comment: Comment; createdAt: Date };

// Comments come from the comments list, each carrying its own createdAt;
// milestones come from the audit events. The two are never paired
// positionally: audit writes can be dropped server-side, so a `comment_added`
// event is a signal that a comment exists, not a handle to a particular one,
// and a missing event used to shift every later comment's timestamp (or drop
// a comment entirely).
function buildTimeline(events: ReportEvent[], comments: Comment[]): TimelineEntry[] {
  const entries: TimelineEntry[] = [];

  for (const event of events) {
    const ts = new Date(event.createdAt);
    if (event.eventType === "report_created") {
      entries.push({ kind: "milestone", icon: "created", description: event.description, createdAt: ts });
    } else if (event.eventType === "status_changed") {
      // Server-side description already includes the resolved label; check that for the icon variant.
      const isResolved = event.description.toLowerCase().includes("resolved");
      entries.push({ kind: "milestone", icon: isResolved ? "resolved" : "status", description: event.description, createdAt: ts });
    }
  }

  for (const comment of comments) {
    entries.push({ kind: "comment", comment, createdAt: new Date(comment.createdAt) });
  }

  entries.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
  return entries;
}

export default function MyReportsPage() {
  const search = useSearch();
  const token = new URLSearchParams(search).get("token");
  const [linkRequested, setLinkRequested] = useState(false);

  const form = useForm<LookupFormValues>({
    resolver: zodResolver(lookupSchema),
    defaultValues: { email: "" },
  });

  const requestLink = useRequestReportLookupLink();

  const { data: reports, isLoading, isError, error } = useLookupReports(
    { token: token ?? "" },
    { query: { enabled: !!token, retry: false, queryKey: getLookupReportsQueryKey({ token: token ?? "" }) } }
  );
  const { data: statuses } = useListStatuses({ scope: "reports" });
  const { data: categories } = useListCategories();
  const statusById = new Map<number, Status>((statuses ?? []).map((s) => [s.id, s]));
  const categoryById = new Map<number, Category>((categories ?? []).map((c) => [c.id, c]));

  const onSubmit = (data: LookupFormValues) => {
    trackEvent("my_reports_lookup");
    requestLink.mutate(
      { data: { email: data.email } },
      { onSuccess: () => setLinkRequested(true) },
    );
  };

  const getStatusBadge = (statusId: number) => {
    const status = statusById.get(statusId);
    if (!status) return <Badge variant="secondary">—</Badge>;
    switch (status.key) {
      case "new":
        return <Badge variant="secondary" className="bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">{status.label}</Badge>;
      case "in_review":
        return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400">{status.label}</Badge>;
      case "resolved":
        return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400">{status.label}</Badge>;
      default:
        return <Badge variant="outline">{status.label}</Badge>;
    }
  };

  // Only a typed 401/403 from the API means the link itself is bad; a network
  // failure or a 5xx is a generic load error, not an expired link.
  const expiredOrInvalid = error instanceof ApiError && [401, 403].includes(error.status);

  return (
    <Layout>
      <PageHead
        title="Check Your MOTUS Bug Reports"
        description="Look up the status of bug reports you submitted to the MOTUS Bug Board using a secure email link."
        path="/my-reports"
      />
      <div className="max-w-3xl mx-auto space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight mb-2">
            Check Your Reports
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            {token
              ? "Here are the bug reports linked to your email."
              : "Enter the email address you used to submit a bug report and we'll send you a secure access link."}
          </p>
        </div>

        {!token && (
          <Card className="border-slate-200 dark:border-slate-800 shadow-sm max-w-md mx-auto">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <CardContent className="pt-6">
                  {linkRequested ? (
                    <div className="text-center py-4" data-testid="text-link-sent">
                      <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
                      <h3 className="font-semibold text-slate-800 dark:text-slate-200 mb-1">Check your email</h3>
                      <p className="text-sm text-slate-500">
                        If any reports were submitted with that address, we've emailed you a secure link to view them. The link is valid for 7 days.
                      </p>
                      <Button
                        type="button"
                        variant="ghost"
                        className="mt-3 text-sm"
                        onClick={() => setLinkRequested(false)}
                        data-testid="button-request-again"
                      >
                        Use a different email
                      </Button>
                    </div>
                  ) : (
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email Address</FormLabel>
                          <FormControl>
                            <div className="flex gap-2">
                              <div className="relative flex-1">
                                <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                                <Input placeholder="you@carrier.com" className="pl-9" {...field} data-testid="input-lookup-email" />
                              </div>
                              <Button type="submit" disabled={requestLink.isPending} className="bg-primary hover:bg-primary/90 text-white shrink-0" data-testid="button-lookup">
                                <Send className="w-4 h-4 mr-2 hidden sm:inline" /> {requestLink.isPending ? "Sending…" : "Email me a link"}
                              </Button>
                            </div>
                          </FormControl>
                          <FormMessage />
                          {requestLink.isError && (
                            <p className="text-sm text-red-600 dark:text-red-400 mt-2" data-testid="text-request-error">
                              We couldn't send the link right now. Please try again in a minute.
                            </p>
                          )}
                        </FormItem>
                      )}
                    />
                  )}
                </CardContent>
              </form>
            </Form>
          </Card>
        )}

        {token && (
          <div className="mt-8">
            {isLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-40 w-full rounded-xl" />
                <Skeleton className="h-40 w-full rounded-xl" />
              </div>
            ) : isError ? (
              <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-lg text-center" data-testid="text-token-error">
                {expiredOrInvalid
                  ? "This access link is invalid or has expired. "
                  : "Failed to load reports. "}
                <a href="/my-reports" className="underline font-medium">Request a new link</a>
              </div>
            ) : reports?.length === 0 ? (
              <div className="text-center py-12 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-dashed border-slate-200 dark:border-slate-800">
                <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300">No reports found</h3>
                <p className="text-sm text-slate-500">We couldn't find any bug reports associated with that email.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {reports?.map((report) => {
                  const timeline = buildTimeline(
                    (report.events ?? []) as ReportEvent[],
                    (report.comments ?? []) as Comment[],
                  );
                  const hasActivity = timeline.length > 0;

                  return (
                    <Card key={report.id} className="border-slate-200 dark:border-slate-800 overflow-hidden" data-testid={`card-report-${report.id}`}>
                      <div className="bg-slate-50 dark:bg-slate-900/50 px-5 py-3 border-b flex flex-wrap justify-between items-center gap-2">
                        <div className="flex items-center gap-3 flex-wrap">
                          {getStatusBadge(report.statusId)}
                          {report.categoryId != null && categoryById.get(report.categoryId) && (
                            <Badge variant="outline" className="text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 flex items-center gap-1 text-xs font-medium">
                              <Tag className="w-3 h-3" />
                              {categoryById.get(report.categoryId)!.name}
                            </Badge>
                          )}
                          <span className="text-xs font-medium text-slate-500 flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            Submitted {format(new Date(report.createdAt), "MMM d, yyyy")}
                          </span>
                        </div>
                        <span className="text-xs font-mono bg-slate-200 dark:bg-slate-800 px-2 py-1 rounded text-slate-600 dark:text-slate-300">
                          DOT: {report.dotNumber}
                        </span>
                      </div>

                      <CardContent className="p-5">
                        <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap text-sm leading-relaxed">
                          {report.description}
                        </p>

                        <div className="mt-4">
                          <AttachmentList reportId={report.id} token={token ?? undefined} />
                        </div>

                        {hasActivity && (
                          <div className="mt-6 pt-6 border-t border-slate-100 dark:border-slate-800">
                            <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2 mb-4">
                              <Clock className="w-4 h-4" /> Activity
                            </h4>

                            <ol className="relative border-l border-slate-200 dark:border-slate-700 ml-2 space-y-5">
                              {timeline.map((entry, i) => {
                                if (entry.kind === "milestone") {
                                  const dotColor =
                                    entry.icon === "created"
                                      ? "bg-blue-400"
                                      : entry.icon === "resolved"
                                        ? "bg-emerald-400"
                                        : "bg-amber-400";
                                  const Icon =
                                    entry.icon === "created"
                                      ? FileText
                                      : entry.icon === "resolved"
                                        ? CheckCircle2
                                        : ArrowRight;
                                  return (
                                    <li key={i} className="ml-5">
                                      <div className={`absolute -left-1.5 mt-1 w-3 h-3 rounded-full border-2 border-white dark:border-slate-900 ${dotColor}`} />
                                      <div className="flex items-start gap-2">
                                        <Icon className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                                        <div>
                                          <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                            {entry.description}
                                          </p>
                                          <p className="text-[11px] text-slate-400 mt-0.5">
                                            {format(entry.createdAt, "MMM d, yyyy 'at' p")}
                                          </p>
                                        </div>
                                      </div>
                                    </li>
                                  );
                                }

                                return (
                                  <li key={i} className="ml-5">
                                    <div className="absolute -left-1.5 mt-1 w-3 h-3 rounded-full border-2 border-white dark:border-slate-900 bg-[var(--dna-indigo-400)]" />
                                    <div
                                      className={`p-3 rounded-lg text-sm ${
                                        entry.comment.isStaff
                                          ? "bg-amber-50/50 dark:bg-amber-950/20 border border-amber-100 dark:border-amber-900/50"
                                          : "bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800"
                                      }`}
                                    >
                                      <div className="flex justify-between items-start mb-1.5">
                                        <div className="flex items-center gap-2">
                                          <span className="font-bold text-slate-900 dark:text-slate-100 text-xs">
                                            {entry.comment.authorName}
                                          </span>
                                          {entry.comment.isStaff && (
                                            <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100 text-[10px] uppercase py-0 px-1.5 rounded-sm flex gap-1">
                                              <ShieldAlert className="w-3 h-3" /> Staff
                                            </Badge>
                                          )}
                                        </div>
                                        <span className="text-[10px] text-slate-400 shrink-0 ml-2">
                                          {format(entry.createdAt, "MMM d, h:mm a")}
                                        </span>
                                      </div>
                                      <p className="text-slate-600 dark:text-slate-300 whitespace-pre-wrap text-sm leading-relaxed">
                                        {entry.comment.body}
                                      </p>
                                    </div>
                                  </li>
                                );
                              })}
                            </ol>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
